class OurPartnersCanvas {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.our-partners-container');
    this.canvas = section.querySelector('[data-partners-canvas]');
    this.header = section.querySelector('[data-partners-header]');
    this.grid = section.querySelector('[data-partners-grid]');
    this.partnerItems = section.querySelectorAll('[data-partner-item]');

    // Get partners data
    const dataElement = document.querySelector('[data-partners-data]');
    this.partnersData = dataElement ? JSON.parse(dataElement.textContent) : { blocks: [] };

    // Get configuration from section data attributes
    this.configItemSize = parseInt(section.dataset.itemSize) || 280;
    this.configInitialScale = (parseInt(section.dataset.initialScale) || 45) / 100; // 更高的初始缩放，让一开始就能看清图片
    this.configTargetScale = (parseInt(section.dataset.targetScale) || 75) / 100;

    // Transform state
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.minScale = 0.1;
    this.maxScale = 1.2; // 最大缩放限制（支持到 100%）
    this.targetScale = this.configTargetScale;

    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0;
    this.lastY = 0;

    // Initial pinch state
    this.initialPinchDistance = 0;
    this.initialScale = 1;

    this.init();
  }

  init() {
    this.calculateDynamicSizes();
    this.distributePartners();

    // 直接使用配置的缩放值，不再限制 maxAllowedScale
    this.scale = Math.max(this.minScale, Math.min(this.configInitialScale, this.maxScale));
    this.targetScale = Math.max(this.scale, Math.min(this.configTargetScale, this.maxScale));

    this.setupEventListeners();

    this.updateTransform();

    this.playIntroAnimation();
  }

  /* ============================================
     Dynamic Size Calculation
     ============================================ */

  calculateDynamicSizes() {
    const count = this.partnerItems.length;
    let itemSize = this.configItemSize;

    if (window.innerWidth <= 768) {
      itemSize = Math.max(120, itemSize * 0.6);
    }

    // 设置 CSS 变量
    this.partnerItems.forEach(item => {
      item.style.setProperty('--item-size', `${itemSize}px`);
    });

    this.itemSize = itemSize;
    this.canvasPadding = itemSize * 1.5; // 适中的 padding，让布局紧凑但不拥挤
  }

  /* ============================================
     Random Scatter Distribution Algorithm
     ============================================ */

  // 检查两个矩形是否重叠
  checkOverlap(x1, y1, size1, x2, y2, size2, minGap) {
    const halfSize1 = size1 / 2;
    const halfSize2 = size2 / 2;
    const distX = Math.abs(x1 - x2);
    const distY = Math.abs(y1 - y2);
    const minDistX = halfSize1 + halfSize2 + minGap;
    const minDistY = halfSize1 + halfSize2 + minGap;
    return distX < minDistX && distY < minDistY;
  }

  // 检查位置是否与标题重叠
  checkHeaderOverlap(x, y, itemSize, headerRect, center) {
    if (!headerRect) return false;

    // 大幅减小标题周围的排除区域，让图片可以更靠近标题
    const headerWidth = headerRect.width * 0.5; // 从0.9减小到0.5
    const headerHeight = headerRect.height * 0.5; // 从0.9减小到0.5
    const distX = Math.abs(x - center);
    const distY = Math.abs(y - center);

    return distX < (headerWidth / 2 + itemSize / 2) && distY < (headerHeight / 2 + itemSize / 2);
  }

  distributePartners() {
    const count = this.partnerItems.length;

    if (count === 0) {
      this.canvasSize = 2000;
      this.canvas.style.width = `${this.canvasSize}px`;
      this.canvas.style.height = `${this.canvasSize}px`;
      return;
    }

    // 画布大小：数量多时用更大画布，避免放不下
    const sizeMultiplier = count > 30 ? 4.5 : 3.5;
    const baseCanvasSize = Math.max(2000, Math.sqrt(count) * this.itemSize * sizeMultiplier);
    this.canvasSize = baseCanvasSize;
    this.canvas.style.width = `${this.canvasSize}px`;
    this.canvas.style.height = `${this.canvasSize}px`;

    const center = this.canvasSize / 2;
    const headerRect = this.header ? this.header.getBoundingClientRect() : null;
    const minGap = this.itemSize * 0.35;

    const placedItems = [];
    // 中心区域只放少量项，避免 50+ 项挤在中心导致大量 fallback
    const nearCenterCount = Math.min(count, Math.max(5, Math.min(12, Math.floor(count * 0.35))));

    this.partnerItems.forEach((item, index) => {
      let placed = false;
      let attempts = 0;
      const maxAttempts = 350;

      const shouldBeNearCenter = index < nearCenterCount;
      
      while (!placed && attempts < maxAttempts) {
        attempts++;

        // 随机生成位置（在画布范围内）
        const margin = this.itemSize * 1.5;
        let x, y;
        
        if (shouldBeNearCenter) {
          // 靠近中心的元素，在中心区域周围生成位置
          const centerRadius = this.itemSize * 1.5; // 减小中心区域半径，让元素更靠近中心
          const angle = Math.random() * Math.PI * 2; // 随机角度
          const distance = centerRadius + Math.random() * this.itemSize * 2; // 减小距离中心的最大距离
          x = center + Math.cos(angle) * distance;
          y = center + Math.sin(angle) * distance;
        } else {
          // 其他元素在整个画布范围内随机分布，但也尽量靠近中心
          const distanceFromCenter = margin + Math.random() * (this.canvasSize / 3);
          const angle = Math.random() * Math.PI * 2;
          x = center + Math.cos(angle) * distanceFromCenter;
          y = center + Math.sin(angle) * distanceFromCenter;
        }

        // 检查是否与标题重叠
        if (this.checkHeaderOverlap(x, y, this.itemSize, headerRect, center)) {
          continue;
        }

        // 检查是否与已放置的图片重叠
        let overlaps = false;
        for (const placed of placedItems) {
          if (this.checkOverlap(x, y, this.itemSize, placed.x, placed.y, this.itemSize, minGap)) {
            overlaps = true;
            break;
          }
        }

        if (!overlaps) {
          // 成功找到不重叠的位置
          placedItems.push({ x, y });

          // 应用位置
          item.style.left = `${x}px`;
          item.style.top = `${y}px`;
          item.style.zIndex = 10 + Math.floor(Math.random() * 20);

          // 随机旋转
          const rotation = this.randomInRange(-8, 8);
          item.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;

          placed = true;
        }
      }

      if (!placed) {
        // 用螺旋位置放置，避免重叠
        const fallbackIndex = placedItems.length;
        const goldenAngle = 2.4;
        const radius = this.itemSize * 1.2 + fallbackIndex * (this.itemSize + minGap) * 0.6;
        const angle = fallbackIndex * goldenAngle;
        const x = center + Math.cos(angle) * radius;
        const y = center + Math.sin(angle) * radius;
        placedItems.push({ x, y });
        item.style.left = `${x}px`;
        item.style.top = `${y}px`;
        item.style.zIndex = 10 + Math.floor(Math.random() * 20);
        const rotation = this.randomInRange(-8, 8);
        item.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
      }
    });
  }

  randomInRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  /* ============================================
     Event Listeners
     ============================================ */

  setupEventListeners() {
    // Mouse drag
    this.container.addEventListener('mousedown', this.handleDragStart.bind(this));
    document.addEventListener('mousemove', this.handleDragMove.bind(this));
    document.addEventListener('mouseup', this.handleDragEnd.bind(this));

    // Touch drag
    this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // Mouse wheel zoom (with Control key)
    this.container.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

    // Pinch zoom gestures (iOS Safari)
    this.container.addEventListener('gesturestart', this.handleGestureStart.bind(this));
    this.container.addEventListener('gesturechange', this.handleGestureChange.bind(this));
    this.container.addEventListener('gestureend', this.handleGestureEnd.bind(this));

    // Partner clicks
    this.partnerItems.forEach(item => {
      item.addEventListener('click', (e) => {
        if (!this.isDragging) {
          this.handlePartnerClick(e);
        }
      });
    });
  }

  /* ============================================
     Intro Animation
     ============================================ */

  playIntroAnimation() {
    setTimeout(() => {
      this.partnerItems.forEach((item, index) => {
        setTimeout(() => item.classList.add('visible'), index * 30);
      });
    }, 100);

    setTimeout(() => this.animateZoomIn(), 2000);
  }

  animateZoomIn() {
    const targetScale = this.targetScale;

    if (targetScale <= this.scale) {
      this.scale = targetScale;
      this.updateTransform();
      this.animationComplete = true;
      return;
    }

    const duration = 2500; // 2.5 秒的放大动画，更流畅
    const startScale = this.scale;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeInOutCubic(progress);

      this.scale = startScale + (targetScale - startScale) * eased;

      this.updateTransform();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - enable interaction
        this.animationComplete = true;
      }
    };

    animate();
  }

  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* ============================================
     Drag Handlers
     ============================================ */

  handleDragStart(e) {
    if (e.target.closest('[data-partner-item]')) {
      // Allow clicking on partners
      return;
    }

    this.isDragging = true;
    this.startX = e.clientX - this.translateX;
    this.startY = e.clientY - this.translateY;
    this.container.classList.add('is-dragging');
    e.preventDefault();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    this.translateX = e.clientX - this.startX;
    this.translateY = e.clientY - this.startY;
    this.updateTransform();
  }

  handleDragEnd() {
    this.isDragging = false;
    this.container.classList.remove('is-dragging');
  }

  /* ============================================
     Touch Handlers
     ============================================ */

  handleTouchStart(e) {
    if (e.touches.length === 1) {
      if (e.target.closest('[data-partner-item]')) {
        return;
      }
      const touch = e.touches[0];
      this.isDragging = true;
      this.startX = touch.clientX - this.translateX;
      this.startY = touch.clientY - this.translateY;
      e.preventDefault();
    } else if (e.touches.length === 2) {
      this.isDragging = false;
      this.initialPinchDistance = this.getPinchDistance(e.touches);
      this.initialScale = this.scale;
      e.preventDefault();
    }
  }

  handleTouchMove(e) {
    if (e.touches.length === 1 && this.isDragging) {
      const touch = e.touches[0];
      this.translateX = touch.clientX - this.startX;
      this.translateY = touch.clientY - this.startY;
      this.updateTransform();
      e.preventDefault();
    } else if (e.touches.length === 2) {
      const distance = this.getPinchDistance(e.touches);
      const scale = (distance / this.initialPinchDistance) * this.initialScale;
      this.setScale(scale);
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false;
    }
  }

  getPinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /* ============================================
     Zoom Handlers
     ============================================ */

  handleWheel(e) {
    // Only zoom when Control/Command key is pressed
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();

      const delta = -e.deltaY;
      const scaleChange = delta > 0 ? 1.1 : 0.9;
      const newScale = this.scale * scaleChange;

      this.setScale(newScale);
    }
  }

  handleGestureStart(e) {
    e.preventDefault();
    this.initialScale = this.scale;
  }

  handleGestureChange(e) {
    e.preventDefault();
    const newScale = this.initialScale * e.scale;
    this.setScale(newScale);
  }

  handleGestureEnd(e) {
    e.preventDefault();
  }

  setScale(newScale) {
    this.scale = Math.max(this.minScale, Math.min(this.maxScale, newScale));
    this.updateTransform();
  }

  /* ============================================
     Transform Update
     ============================================ */

  updateTransform() {
    const transform = `translate(calc(-50% + ${this.translateX}px), calc(-50% + ${this.translateY}px)) scale(${this.scale})`;
    this.canvas.style.transform = transform;
  }

  /* ============================================
     Partner Click Handler
     ============================================ */

  handlePartnerClick(e) {
    const partnerItem = e.currentTarget;
    const blockId = partnerItem.dataset.blockId;
    const partnerType = partnerItem.dataset.partnerType;

    // Use strict comparison with string conversion to handle potential type mismatches
    const partnerData = this.partnersData.blocks.find(block => 
      String(block.id) === String(blockId) && block.type === partnerType
    );

    if (partnerData) {
      this.openModal(partnerType, partnerData);
    }
  }

  /* ============================================
     Modal Management
     ============================================ */

  openModal(type, data) {
    const modal = this.section.querySelector(`[data-modal="${type}"]`);
    if (!modal) return;

    // Populate modal content
    if (type === 'roaster') {
      this.populateRoasterModal(modal, data);
    } else if (type === 'curator') {
      this.populateCuratorModal(modal, data);
    }


    // Show modal
    modal.classList.add('active');
    setTimeout(() => modal.classList.add('visible'), 10);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Setup close handlers
    this.setupModalCloseHandlers(modal);
  }

  populateRoasterModal(modal, data) {
    const content = modal.querySelector('[data-roaster-content]');

    let html = '';

    // Logo
    if (data.logo && data.logo.trim() !== '') {
      html += `<img src="${data.logo}" alt="${data.name}" class="roaster-logo">`;
    }

    // Slogan
    if (data.slogan) {
      html += `<h2 class="roaster-slogan xbloom-h7">${data.slogan}</h2>`;
    }

    //caption
    if (data.caption) {
      html += `<div class="roaster-caption xbloom-text-16">${data.caption}</div>`;
    }

     // Content
    if (data.content) {
      html += `<div class="roaster-content xbloom-text-14">${data.content}</div>`;
    }

    // Info (Founder & Location)
    if (data.founder || data.location) {
      html += '<div class="roaster-info xbloom-text-14">';
      if (data.founder) {
        html += `
          <div class="roaster-info-item">
            <div>Founder</div>
            <p>${data.founder}</p>
          </div>
        `;
      }
      if (data.location) {
        html += `
          <div class="roaster-info-item">
            <div>Location</div>
            <p>${data.location}</p>
          </div>
        `;
      }
      html += '</div>';
    }

    // Gallery
    if (data.images && data.images.length > 0) {
      html += '<div class="roaster-gallery">';
      data.images.forEach((imgObj, index) => {
        if (imgObj && imgObj.image && imgObj.image.trim() !== '') {
          html += '<figure class="roaster-gallery-item">';
          html += `<img src="${imgObj.image}" alt="${data.name} - Image ${index + 1}" loading="lazy">`;
          if (imgObj.caption && imgObj.caption.trim() !== '') {
            html += `<figcaption>${imgObj.caption}</figcaption>`;
          }
          html += '</figure>';
        }
      });
      html += '</div>';
    }

    content.innerHTML = html;
  }

  populateCuratorModal(modal, data) {
    const content = modal.querySelector('[data-curator-content]');

    let html = '<div class="curator-header">';

     // Name
     if (data.name) {
      html += `<h2 class="curator-name xbloom-h4">${data.name}</h2>`;
     }

      // Title
      if (data.title) {
        html += `<div class="curator-title xbloom-text-14">${data.title}</div>`;
       }

     // Photo
      if (data.photo && data.photo.trim() !== '') {
        html += `<img src="${data.photo}" alt="${data.name}" class="curator-photo" loading="lazy">`;
      }

       // headline
       if (data.headline) {
        html += `<div class="curator-headline xbloom-text-16">${data.headline}</div>`;
       }

    html += '</div>';

    // Modules
    if (data.modules && data.modules.length > 0) {
      html += '<div class="curator-modules">';
      data.modules.forEach(module => {
        if (module.title || module.description) {
          const hasImage = module.image && module.image.trim() !== '';
          // Ensure description is a string before calling replace
          const descriptionText = module.description ? String(module.description) : '';
          const descriptionWithBreaks = descriptionText.replace(/\n/g, '<br>');
          const imagePosition = module.imagePosition || 'right';
          const positionClass = hasImage ? `image-${imagePosition}` : '';

          html += `<div class="curator-module ${hasImage ? 'has-image' : ''} ${positionClass}">`;

          html += '<div class="curator-module-text">';
          if (module.title) {
            html += `<div class="curator-module-title xbloom-text-16">${module.title}</div>`;
          }
          if (descriptionWithBreaks) {
            html += `<div class="curator-module-description xbloom-text-14">${descriptionWithBreaks}</div>`;
          }
          html += '</div>';

          if (hasImage) {
            html += `<div class="curator-module-image"><img src="${module.image}" alt="${module.title}" loading="lazy"></div>`;
          }

          html += '</div>';
        }
      });
      html += '</div>';
    }

    content.innerHTML = html;
  }

  setupModalCloseHandlers(modal) {
    const closeButtons = modal.querySelectorAll('[data-modal-close]');

    const closeModal = () => {
      modal.classList.remove('visible');
      setTimeout(() => {
        modal.classList.remove('active');
        document.body.style.overflow = '';
      }, 300);
    };

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal, { once: true });
    });

    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }
}

// Initialize when DOM is ready
function initOurPartnersCanvas() {
  const sections = document.querySelectorAll('.our-partners-section');
  sections.forEach((section) => new OurPartnersCanvas(section));
}

// Try multiple initialization methods to ensure it runs
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOurPartnersCanvas);
} else {
  // DOM already loaded (script with defer)
  initOurPartnersCanvas();
}
